// @ts-strict-ignore
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { PermissionsNavigationModel } from '@project/components/permissions/permissions-navigation.model';
import { ProjectGroupLoadingService } from '@project/components/project/service/project-group-loading.service';
import { UserModel } from '@user/model/user.model';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { UserVO } from '@user/model/vo/user.vo';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { firstValueFrom, ReplaySubject, Subject, Subscription } from 'rxjs';
import { SharedProjectGroupInfoVO, SharedProjectInfoVO } from '@project/components/permissions/shared-info.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { UserSettings } from '@user/model/user-settings';
import { ProjectModel } from '@project/model/project.model';
import { FriendSharedInfo } from '@project/components/permissions/friend-shared-info';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { FriendListEntryVO } from '@project/components/permissions/friend-list-entry.vo';
import { FriendInfoVO } from '@project/components/permissions/friend-info.vo';
import { FriendListService } from '@project/components/permissions/service/friend-list.service';
import { UserFriendListContentPresenter } from '@project/components/permissions/user-friend-list-content.presenter';
import { SharedObjectInfoDataService } from '../../project/service/shared-object-info-data.service';
import { ToastrService } from 'ngx-toastr';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectNavigationModelState } from '@project-navigation/service/project-navigation-model-state';
import { UndoRedoPermissionsNavigationManager } from '@project/components/permissions/undo-redo-permissions-navigation.manager';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Log } from '@shared/debug/log';

export class PermissionsNavigationService {
  private undoRedoManager: UndoRedoPermissionsNavigationManager;
  private undoStackChangedSubscription: Subscription;
  private redoStackChangedSubscription: Subscription;

  public friendListContentPresenter: UserFriendListContentPresenter;
  protected friendListByCurrentUserLoaded: Subscription;
  protected sharedObjectInfoLoaded: Subscription;

  constructor(
    private model: PermissionsNavigationModel,
    private userSettings: UserSettings,
    private userModel: UserModel,
    private projectGroupLoadingService: ProjectGroupLoadingService,
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private friendListService: FriendListService,
    private sharedObjectInfoDataService: SharedObjectInfoDataService,
    private toastrService: ToastrService,
    private localeResourceManager: LocaleResourceManager
  ) {
    this.undoRedoManager = new UndoRedoPermissionsNavigationManager(this.model);
  }

  public loadInitialData(): void {

    this.friendListContentPresenter = new UserFriendListContentPresenter(
      this.alertHelper,
      this.projectModel,
      this.friendListService,
      this.sharedObjectInfoDataService,
      this.userModel,
    );
    this.model.friendListContentPresenter = this.friendListContentPresenter;
    this.friendListByCurrentUserLoaded = this.friendListContentPresenter.friendListByCurrentUserLoaded.subscribe(async () => {
      try {
        let projectGroups: ProjectGroupVO[];
        if (this.userModel.user.hasPermission("ACCESS_DEFAULT_IMPORT_PROJECT_GROUP")) {
          projectGroups = await this.projectGroupLoadingService.loadBaseOwnedAndDefaultProjectGroups();
        } else {
          projectGroups = await this.projectGroupLoadingService.loadOwnedBaseProjectGroups();
        }
        await this.baseGroupsSuccessHandler(projectGroups);
      } catch (error) {
        this.baseGroupsFailureHandler();
      }
    });
    this.sharedObjectInfoLoaded = this.friendListContentPresenter.sharedObjectInfoLoaded.subscribe((sharedInfo) => {
      this.sharedInfoLoaded(sharedInfo);
    });
  }

  private async baseGroupsSuccessHandler(projectGroups: ProjectGroupVO[]): Promise<void> {
    const defaultImportGroupVO: ProjectGroupVO = this.findDefaultImportProjectGroup(projectGroups);
    let groups = this.excludeDefaultUserGroups(projectGroups);
    groups = this.excludeMyAssignedProjectGroups(groups);
    groups = this.excludeSharedProjectGroups(groups);
    if (defaultImportGroupVO && this.userModel.user.hasPermission("ACCESS_DEFAULT_IMPORT_PROJECT_GROUP")) {
      groups.push(defaultImportGroupVO);
    }
    groups.sort((a: ProjectGroupVO, b: ProjectGroupVO) => ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id));
    this.model.rootProjectGroup = this.createRootProjectGroup(groups);
    this.model.navigationChain = [this.model.rootProjectGroup];
    this.model.currentProjectGroup = this.model.rootProjectGroup;
    let preselectedProjectGroupId: number = this.friendListContentPresenter.getPreselectedProjectGroupId();
    if (NumberUtils.isNotNumber(preselectedProjectGroupId)) {
      preselectedProjectGroupId = this.userSettings.lastSelectedProjectGroupId;
    }
    if (NumberUtils.isNumber(preselectedProjectGroupId)) {
      try {
        const projectGroups = await this.projectGroupLoadingService.loadPreselectedProjectGroups(preselectedProjectGroupId);
        if (ArrayUtils.isNotEmpty(projectGroups) && this.model.rootProjectGroup.groups.find((g) => g.id === projectGroups[0].id)) {
          this.addPreselectedChainToModel(projectGroups);
        }
      } catch (error) {
        Log.error(error);
      }
    }
  }

  private baseGroupsFailureHandler() {
    this.model.rootProjectGroup = this.createRootProjectGroup();
    this.model.navigationChain = [this.model.rootProjectGroup];
    this.model.currentProjectGroup = this.model.rootProjectGroup;
  }

  public loadProjectGroup(selectedProjectGroup: ProjectGroupVO, openGroup: boolean = true): Promise<void> {
    // finding original, not deep clone
    let targetProjectGroup = this.model.navigationChain[this.model.navigationChain.length - 1].groups.find(
      (group: ProjectGroupVO) => group.id === selectedProjectGroup.id
    );

    if (!targetProjectGroup) {
      targetProjectGroup = selectedProjectGroup;
    }

    const addToNavChain = () => {
      this.model.currentProjectGroup = targetProjectGroup;
      const oldNavState = [...this.model.navigationChain];
      const newNavState = [...oldNavState, targetProjectGroup];
      this.undoRedoManager.pushUndoOperation(
        {
          navigationChain: oldNavState,
          rootSearchProjectGroup: this.model.rootSearchProjectGroup,
          searchMode: this.model.searchMode,
        },
        {
          navigationChain: newNavState,
          rootSearchProjectGroup: this.model.rootSearchProjectGroup,
          searchMode: this.model.searchMode,
          searchText: this.model.searchText,
        }
      );
      this.model.navigationChain = newNavState;
    };

    if (this.model.searchMode || targetProjectGroup.childContentLoaded) {
      // search result is already loaded as full-tree
      if (targetProjectGroup.groupType !== 3 && openGroup) {
        addToNavChain();
      } else {
        this.model.currentChanged.next(this.model.currentProjectGroup);
      }
      return Promise.resolve();
    }

    return this.projectGroupLoadingService
      .loadProjectGroupById(targetProjectGroup.id, 1)
      .then((loadedProjectGroup: ProjectGroupVO) => {
        this.addLoadedProjectGroupToData(targetProjectGroup, loadedProjectGroup);
        if (targetProjectGroup.groupType !== 3 && openGroup) {
          addToNavChain();
        } else {
          this.addLoadedProjectGroupToData(
            this.model.currentProjectGroup.groups.find((g) => g.id === loadedProjectGroup.id),
            loadedProjectGroup
          );
          this.model.currentChanged.next(this.model.currentProjectGroup);
        }
      })
      .catch((e) => Log.info(e));
  }

  public selectItem(item: ProjectHeadVO | ProjectGroupVO): Subject<any> {
    this.model.currentSelectedItem = item;
    return this.updateRightsBySelectedNode();
  }

  public navigateInChain(projectGroup: ProjectGroupVO): void {
    const index = this.model.navigationChain.indexOf(projectGroup);
    if (index === this.model.navigationChain.length - 1) {
      return;
    }
    const oldNavState = [...this.model.navigationChain];
    const newNavState = [...oldNavState.slice(0, index + 1)];
    this.undoRedoManager.pushUndoOperation(
      {
        navigationChain: oldNavState,
        rootSearchProjectGroup: this.model.rootSearchProjectGroup,
        searchMode: this.model.searchMode,
      },
      {
        navigationChain: newNavState,
        rootSearchProjectGroup: this.model.rootSearchProjectGroup,
        searchMode: this.model.searchMode,
        searchText: this.model.searchText,
      }
    );
    this.model.navigationChain = newNavState;
    this.model.currentProjectGroup = projectGroup;
  }

  public async performUndo(): Promise<void> {
    await this.undoRedoManager.undo();
  }

  public async performRedo(): Promise<void> {
    await this.undoRedoManager.redo();
  }

  public removeUserFromFriendList(friend: FriendSharedInfo) {
    if (!friend) {
      return;
    }
    this.alertHelper
      .showAlert(
        'shareProjectPopup.remove.title.remove_user_from_friendList',
        'shareProjectPopup.remove.message.remove_user_from_friendList',
        { 0: friend.username },
        [AlertButton.YES, AlertButton.NO],
        "help"
      )
      .then((answer) => {
        if (answer === AlertButton.YES) {
          const friendListEntry: FriendListEntryVO = new FriendListEntryVO();
          const friendInfoVO: FriendInfoVO = new FriendInfoVO();
          friendInfoVO.id = friend.userId;
          friendInfoVO.username = friend.username;
          friendListEntry.userId = this.friendListContentPresenter.currentUser.id;
          friendListEntry.friendInfo = friendInfoVO;
          this.friendListService
            .removeUserFromFriendList(friendListEntry)
            .toPromise()
            .then((result) => {
              this.friendListContentPresenter.removeSharedFriendInfo(friendListEntry);
            })
            .catch((e) => {});
        }
      });
  }

  public readCheckBoxChanged(friendSharedInfo: FriendSharedInfo) {
    if (friendSharedInfo.isReadable) {
      this.askUserAboutCascadeRightsUpdating([friendSharedInfo]);
    } else {
      this.askUserAboutCascadeRightsRemoving([friendSharedInfo]);
    }
  }

  public editCheckBoxChanged(friendSharedInfo: FriendSharedInfo) {
    this.askUserAboutCascadeRightsUpdating([friendSharedInfo]);
  }

  private createRootProjectGroup(projectGroups: ProjectGroupVO[] = [], projects: ProjectVO[] = []): ProjectGroupVO {
    const projectGroup = new ProjectGroupVO();
    projectGroup.id = PermissionsNavigationModel.ROOT_ID;
    projectGroup.groupName = PermissionsNavigationModel.ROOT_NAME;
    projectGroup.groups = projectGroups;
    projectGroup.projects = projects;
    return projectGroup;
  }

  private findDefaultImportProjectGroup(groups: ProjectGroupVO[]): ProjectGroupVO {
    let defaultImportGroupVO: ProjectGroupVO = null;
    if (ArrayUtils.isEmpty(groups)) {
      return defaultImportGroupVO;
    }
    for (let i = 0; i < groups.length; i++) {
      if (ProjectGroupUtils.isDefaultImportProjectGroup(groups[i])) {
        defaultImportGroupVO = groups[i];
        groups.splice(i, 1);
        break;
      }
    }
    return defaultImportGroupVO;
  }

  private excludeDefaultUserGroups(groups: ProjectGroupVO[]): ProjectGroupVO[] {
    return groups.filter((g) => g.groupOwner.userId !== UserVO.DEFAULT_GUEST_ACCOUNT_ID);
  }

  private excludeMyAssignedProjectGroups(groups: ProjectGroupVO[]): ProjectGroupVO[] {
    return groups.filter((g) => !ProjectGroupUtils.isMyAssignedProjectsGroup(g));
  }

  private excludeSharedProjectGroups(groups: ProjectGroupVO[]): ProjectGroupVO[] {
    return groups.filter((g) => g.groupOwner.userId === this.userModel.user.id);
  }

  private addPreselectedChainToModel(preselectedProjectGroups: ProjectGroupVO[]) {
    let projectGroupFromData = this.model.rootProjectGroup;
    let groupToCollapse: ProjectGroupVO;
    while (preselectedProjectGroups.length) {
      const projectGroupFromArray = preselectedProjectGroups.shift();
      const currentProjectGroup = projectGroupFromData.groups.find((projectGroup) => projectGroup.guid === projectGroupFromArray.guid);
      currentProjectGroup.groups = projectGroupFromArray.groups.sort((a: ProjectGroupVO, b: ProjectGroupVO) =>
        ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id)
      );
      currentProjectGroup.projects = projectGroupFromArray.projects.sort(ProjectUtils.projectSortByNameFunction);
      currentProjectGroup.childContentLoaded = projectGroupFromArray.childContentLoaded;
      if (currentProjectGroup.groupType !== ProjectGroupUtils.PROJECT) {
        this.model.navigationChain.push(currentProjectGroup);
        projectGroupFromData = currentProjectGroup;
      } else {
        this.loadProjectGroup(currentProjectGroup);
        groupToCollapse = currentProjectGroup;
      }
    }
    this.model.currentProjectGroup = projectGroupFromData;
    if (groupToCollapse) {
      this.model.collapsedProjects.push(groupToCollapse.id);

      let selectSubject: Subject<any>;
      // preselect project
      if (this.projectModel.currentProject) {
        const preselectedProject: ProjectHeadVO = groupToCollapse.projects.find((p) => p.id === this.projectModel.currentProject.id);
        if (preselectedProject) {
          selectSubject = this.selectItem(preselectedProject);
        }
      }
      if (!this.model.currentSelectedItem) {
        selectSubject = this.selectItem(groupToCollapse);
      }

      const emitCurrentChanged = () => this.model.currentChanged.next(this.model.currentProjectGroup);
      selectSubject ? selectSubject.subscribe(() => emitCurrentChanged()) : emitCurrentChanged();
    }
  }

  private addLoadedProjectGroupToData(targetProjectGroup: ProjectGroupVO, loadedProjectGroup: ProjectGroupVO): void {
    targetProjectGroup.groups = loadedProjectGroup.groups.sort((a: ProjectGroupVO, b: ProjectGroupVO) =>
      ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id)
    );
    targetProjectGroup.projects = loadedProjectGroup.projects.sort((a: ProjectHeadVO, b: ProjectHeadVO) =>
      ProjectUtils.projectSortByNameFunction(a, b));
    targetProjectGroup.childContentLoaded = true;
  }

  private sharedInfoLoaded(sharedInfo): void {
    if (sharedInfo) {
      if (sharedInfo instanceof SharedProjectInfoVO) {
        this.loadSharedProjectInfo_success(sharedInfo);
      } else {
        this.loadSharedProjectGroupInfo_success(sharedInfo);
      }
    }
  }

  private loadSharedProjectInfo_success(sharedInfo: SharedProjectInfoVO): void {
    this.friendListContentPresenter.processLoadedSharedInfo(sharedInfo, this.model.currentSelectedItem);
    this.updateAllRightCheckBoxStates();
  }

  public loadSharedProjectGroupInfo_success(sharedInfo: SharedProjectGroupInfoVO): void {
    this.friendListContentPresenter.processLoadedSharedInfo(sharedInfo, this.model.currentSelectedItem);
    this.updateAllRightCheckBoxStates();
  }

  protected updateAllRightCheckBoxStates(): void {
    this.updateAllReadCheckBoxState();
    this.updateAllEditCheckBoxState();
  }

  private updateAllReadCheckBoxState(): void {
    let usersWithReadRightCount: number = 0;
    for (const friendSharedInfo of this.friendListContentPresenter.userFriends) {
      if (friendSharedInfo.isReadable) {
        usersWithReadRightCount++;
      }
    }
    this.model.selectAllReadCheckBox = this.updateCheckBoxState(usersWithReadRightCount, this.friendListContentPresenter.userFriends.length);
  }

  private updateAllEditCheckBoxState(): void {
    let usersWithEditRightCount: number = 0;
    for (const friendSharedInfo of this.friendListContentPresenter.userFriends) {
      if (friendSharedInfo.isEditable) {
        usersWithEditRightCount++;
      }
    }
    this.model.selectAllEditCheckBox = this.updateCheckBoxState(usersWithEditRightCount, this.friendListContentPresenter.userFriends.length);
  }

  private updateCheckBoxState(usersWithRightsCount: number, totalUserCount: number): boolean {
    if (totalUserCount === usersWithRightsCount) {
      return true;
    }
    return false;
  }

  public selectAllEditChanged(): void {
    const selected: boolean = this.model.selectAllEditCheckBox;
    for (const friendSharedInfo of this.friendListContentPresenter.userFriends) {
      friendSharedInfo.isEditable = selected;
    }
    this.askUserAboutCascadeRightsUpdating(this.friendListContentPresenter.userFriends);
  }

  public selectAllReadChanged(): void {
    if (ArrayUtils.isEmpty(this.friendListContentPresenter.userFriends)) {
      return;
    }

    const selected: boolean = this.model.selectAllReadCheckBox;
    if (selected) {
      const friendSharedInfoToUpdate: FriendSharedInfo[] = [];
      for (const friendSharedInfo of this.friendListContentPresenter.userFriends) {
        if (!friendSharedInfo.isReadable) {
          friendSharedInfo.isReadable = true;
          friendSharedInfoToUpdate.push(friendSharedInfo);
        }
      }
      this.askUserAboutCascadeRightsUpdating(friendSharedInfoToUpdate);
    } else {
      this.askUserAboutCascadeRightsRemoving(this.friendListContentPresenter.userFriends);
    }
  }

  private askUserAboutCascadeRightsUpdating(friendSharedInfos: FriendSharedInfo[]): void {
    if (ArrayUtils.isEmpty(friendSharedInfos)) {
      return;
    }
    if (
      !this.model.currentSelectedItem ||
      this.model.currentSelectedItem instanceof ProjectHeadVO ||
      (ArrayUtils.isEmpty(this.model.currentSelectedItem.groups) && ArrayUtils.isEmpty(this.model.currentSelectedItem.projects))
    ) {
      for (const friendSharedInfo of friendSharedInfos) {
        friendSharedInfo.updateChildrenAvailable = false;
      }
      this.friendListContentPresenter.applyChangedSharedUserRightsToUploadedNodes(this.model.currentSelectedItem, friendSharedInfos);
      this.updateUserInterface();
      return;
    }

    this.alertHelper
      .showAlert(
        'shareProjectPopup.alert.title.update_shared_rights_for_children',
        'shareProjectPopup.alert.message.update_shared_rights_for_children',
        null,
        [AlertButton.YES, AlertButton.NO],
        "info"
      )
      .then((answer) => {
        let updateChildrenAvailable: boolean = false;
        if (answer === AlertButton.YES) {
          updateChildrenAvailable = true;
        }
        for (const friendSharedInfo of friendSharedInfos) {
          friendSharedInfo.updateChildrenAvailable = updateChildrenAvailable;
        }
        this.friendListContentPresenter.applyChangedSharedUserRightsToUploadedNodes(this.model.currentSelectedItem, friendSharedInfos);
        this.updateUserInterface();
      });
  }

  private askUserAboutCascadeRightsRemoving(friendSharedInfos: FriendSharedInfo[]): void {
    if (ArrayUtils.isEmpty(friendSharedInfos)) {
      return;
    }
    if (
      !this.model.currentSelectedItem ||
      this.model.currentSelectedItem instanceof ProjectHeadVO ||
      (ArrayUtils.isEmpty(this.model.currentSelectedItem.groups) && ArrayUtils.isEmpty(this.model.currentSelectedItem.projects))
    ) {
      for (const friendSharedInfo of friendSharedInfos) {
        friendSharedInfo.isReadable = false;
        friendSharedInfo.updateChildrenAvailable = true;
      }
      this.friendListContentPresenter.applyChangedSharedUserRightsToUploadedNodes(this.model.currentSelectedItem, friendSharedInfos);
      this.updateUserInterface();
      return;
    }

    this.alertHelper
      .showAlert(
        'shareProjectPopup.alert.title.remove_shared_rights_for_children',
        'shareProjectPopup.alert.message.remove_shared_rights_for_children',
        null,
        [AlertButton.YES, AlertButton.NO],
        "info"
      )
      .then((answer) => {
        let updateChildrenAvailable: boolean = false;
        if (answer === AlertButton.YES) {
          updateChildrenAvailable = true;
        }
        for (const friendSharedInfo of friendSharedInfos) {
          friendSharedInfo.isReadable = false;
          friendSharedInfo.updateChildrenAvailable = updateChildrenAvailable;
        }
        this.friendListContentPresenter.applyChangedSharedUserRightsToUploadedNodes(this.model.currentSelectedItem, friendSharedInfos);
        this.updateUserInterface();
      });
  }

  private updateUserInterface(): void {
    this.updateAllRightCheckBoxStates();
  }

  public async performSearchProject(searchText: string): Promise<void> {
    const actualValue = searchText.trim();
    if (StringUtils.isEmpty(actualValue)) {
      this.model.searchMode = false;
      this.model.searchText = '';
      const lastState: ProjectNavigationModelState = this.undoRedoManager.getLastState();
      if (lastState) {
        this.model.navigationChain = lastState.navigationChain;
        this.model.currentProjectGroup = lastState.navigationChain[lastState.navigationChain.length - 1];
      }
      return;
    }

    try {
      const projectGroups = await this.projectGroupLoadingService.searchProjectGroups(actualValue);
      this.searchSuccessHandler(actualValue, projectGroups);
    } catch (error) {
      Log.error(error);
    }
  }

  private searchSuccessHandler(actualValue: string, projectGroups: ProjectGroupVO[]) {
    this.model.searchText = actualValue;
    this.addSearchResultToModel(projectGroups);
  }

  private addSearchResultToModel(projectGroups: ProjectGroupVO[]): void {
    const oldNavState = {
      navigationChain: this.model.navigationChain,
      rootSearchProjectGroup: this.model.rootSearchProjectGroup,
      searchMode: this.model.searchMode,
    };
    this.model.searchMode = true;
    let filteredGroups: ProjectGroupVO[] = this.excludeDefaultUserGroups(projectGroups);
    if (this.friendListContentPresenter.currentUser.hasPermission("ACCESS_DEFAULT_IMPORT_PROJECT_GROUP")) {
      filteredGroups = filteredGroups.filter(
        (g) => this.isProjectGroupInsideDefaultImportGroup(g) || this.friendListContentPresenter.currentUser.id === g.groupOwner.userId,
      );
    } else {
      filteredGroups = this.excludeSharedProjectGroups(filteredGroups);
    }
    let data = this.parseToTree(filteredGroups);
    data = this.excludeMyAssignedProjectGroups(data);
    this.model.rootSearchProjectGroup = this.createRootProjectGroup(data);
    this.model.navigationChain = [this.model.rootSearchProjectGroup];
    this.model.currentProjectGroup = this.model.rootSearchProjectGroup;
    this.undoRedoManager.pushUndoOperation(oldNavState, {
      navigationChain: this.model.navigationChain,
      rootSearchProjectGroup: this.model.rootSearchProjectGroup,
      searchMode: this.model.searchMode,
      searchText: this.model.searchText,
    });
  }

  private isProjectInsideDefaultImportGroup(projectHead: ProjectHeadVO): boolean {
    return this.isProjectGroupInsideDefaultImportGroup(projectHead.projectGroup);
  }

  private isProjectGroupInsideDefaultImportGroup(projectGroup: ProjectGroupVO): boolean {
    if (!projectGroup) {
      return false;
    }
    let parentProjectGroup: ProjectGroupVO = projectGroup;
    while (parentProjectGroup.parentGroup) {
      parentProjectGroup = parentProjectGroup.parentGroup;
    }
    return ProjectGroupUtils.isDefaultImportProjectGroup(parentProjectGroup);
  }

  private parseToTree(projectGroups: ProjectGroupVO[]): ProjectGroupVO[] {
    let data = projectGroups;
    let previousLength: number;
    do {
      previousLength = data.length;
      data = this.parseToTreeIteration(data);
    } while (data.length < previousLength);
    return data;
  }

  private parseToTreeIteration(projectGroups: ProjectGroupVO[]): ProjectGroupVO[] {
    let data = projectGroups;
    projectGroups.forEach((pg: ProjectGroupVO, index: number) => {
      if (!pg.parentGroup) {
        return;
      }
      const parent = ProjectGroupUtils.findProjectGroupInATree(projectGroups, pg.parentGroup.id);
      if (!parent) {
        return;
      }
      const targetChild = parent.groups.find((group: ProjectGroupVO) => group.id === pg.id);
      if (targetChild) {
        targetChild.groups = [...targetChild.groups, ...pg.groups].sort((a: ProjectGroupVO, b: ProjectGroupVO) =>
          ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id)
        );
        targetChild.projects = [...targetChild.projects, ...pg.projects].sort(ProjectUtils.projectSortByNameFunction);
      } else {
        parent.groups.push(pg);
        parent.groups.sort((a: ProjectGroupVO, b: ProjectGroupVO) => ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id));
      }
      data = data.filter((group: ProjectGroupVO) => group.id !== pg.id);
    });
    return data;
  }

  public async performSearchUser(friendName: string): Promise<void> {
    if (
      !this.friendListContentPresenter.currentUser.hasPermission("SEARCH_FOR_FRIENDS_BY_USERNAME") ||
      !this.friendListContentPresenter.currentUser.hasPermission("ADD_FRIENDS") ||
      StringUtils.isEmpty(friendName)
    ) {
      return;
    }

    if (this.friendListContentPresenter.isFriendInList(friendName)) {
      await this.alertHelper.showError('shareProjectPopup.alert.title.adding_new_friend', 'shareProjectPopup.alert.message.friend_already_exists');
      return;
    }
    try {
      const friendInfos = await firstValueFrom(this.friendListService.findUsersAsFriendsByUsername(friendName));
      this.searchForFriendsByUsername_success(friendInfos);
    } catch (error: any) {
      const userCanNotBeFriendErrorMessage: string = 'shareProjectPopup.alert.message.user_can_not_be_friend';
      const faultText: string = error.error?.message ?? "";
      if (faultText.indexOf(userCanNotBeFriendErrorMessage) !== -1) {
        await this.alertHelper.showAlert('global.label.hint', userCanNotBeFriendErrorMessage, {0: friendName}, [AlertButton.OK], "info");
      } else {
        await this.alertHelper.showError('global.title.error', error.toString());
      }
    }
  }

  public async searchForFriendsByUsername_success(friendInfos: FriendInfoVO[]): Promise<void> {
    if (ArrayUtils.isNotEmpty(friendInfos)) {
      const friendInfoToAdd: FriendInfoVO = friendInfos[0];
      this.addUserToFriendList(friendInfoToAdd);
    } else {
      await this.alertHelper.showError('shareProjectPopup.alert.title.adding_new_friend', 'shareProjectPopup.alert.message.friend_not_found');
    }
  }

  private addUserToFriendList(newFriend: FriendInfoVO): void {
    if (!newFriend) {
      return;
    }
    const friendListEntry: FriendListEntryVO = new FriendListEntryVO();
    friendListEntry.userId = this.friendListContentPresenter.currentUser.id;
    friendListEntry.friendInfo = newFriend;
    this.friendListService
      .addUserToFriendList(friendListEntry)
      .toPromise()
      .then((entry) => this.addUserToFriendList_success(entry))
      .catch((e) => {});
  }

  public addUserToFriendList_success(friendListEntry: FriendListEntryVO): void {
    if (!friendListEntry) {
      return;
    }
    this.friendListContentPresenter.addUserToFriendList(friendListEntry, this.model.currentSelectedItem);
    this.updateUserInterface();
  }

  public initSubscribtions(): void {
    this.undoStackChangedSubscription = this.undoRedoManager.undoStackChanged.subscribe((value: boolean) => {
      this.model.canUndo = value;
    });
    this.redoStackChangedSubscription = this.undoRedoManager.redoStackChanged.subscribe((value: boolean) => {
      this.model.canRedo = value;
    });
  }

  public destroySubscribtions(): void {
    if (this.undoStackChangedSubscription) {
      this.undoStackChangedSubscription.unsubscribe();
    }
    if (this.redoStackChangedSubscription) {
      this.redoStackChangedSubscription.unsubscribe();
    }
    if (this.friendListByCurrentUserLoaded) {
      this.friendListByCurrentUserLoaded.unsubscribe();
    }
    if (this.sharedObjectInfoLoaded) {
      this.sharedObjectInfoLoaded.unsubscribe();
    }
  }

  public saveLastSelectedShareProjectGroup(): void {
    if (this.model.currentSelectedItem) {
      const projectId: number =
        this.model.currentSelectedItem instanceof ProjectGroupVO ? this.model.currentSelectedItem.id : this.model.currentSelectedItem.projectGroup.id;
      this.userSettings.setUserSettingValue("lastSelectedProjectGroupId", projectId);
      this.userSettings.saveUserSettings();
    }
  }

  public saveChangedSharedRights(isDefaultImportGroupUpdateCheckRequired: boolean): void {
    const sharedProjectInfos: SharedProjectInfoVO[] = this.friendListContentPresenter.getProjectSharedRightsToSave();
    const sharedProjectGroupInfos: SharedProjectGroupInfoVO[] = this.friendListContentPresenter.getProjectGroupSharedRightsToSave();
    let updateSharedRightsForDefaultImportGroup: boolean = false;
    if (isDefaultImportGroupUpdateCheckRequired) {
      for (const sharedProjectGroupInfo of sharedProjectGroupInfos) {
        const group: ProjectGroupVO = this.findGroupById(sharedProjectGroupInfo.objectId, this.model.rootProjectGroup.groups);
        if (this.isProjectGroupInsideDefaultImportGroup(group)) {
          updateSharedRightsForDefaultImportGroup = true;
          break;
        }
      }
      if (!updateSharedRightsForDefaultImportGroup) {
        for (const sharedProjectInfo of sharedProjectInfos) {
          const project: ProjectHeadVO = this.findProjectById(sharedProjectInfo.objectId, this.model.rootProjectGroup.groups);
          if (this.isProjectInsideDefaultImportGroup(project)) {
            updateSharedRightsForDefaultImportGroup = true;
            break;
          }
        }
      }
    }
    this.sharedObjectInfoDataService
      .saveSharedObjectInfo(sharedProjectInfos, sharedProjectGroupInfos)
      .toPromise()
      .then((isObjectsSaved) => {
        if (isObjectsSaved) {
          this.saveSharedUserRights_success(updateSharedRightsForDefaultImportGroup);
        } else {
          this.saveSharedObjectInfo_failure('shareProjectPopup.alert.message.save_shared_rights_failure_reason');
        }
      })
      .catch((e) => this.saveSharedObjectInfo_failure(''));
  }

  private saveSharedUserRights_success(updateSharedRightsForDefaultImportGroup: boolean): void {
    this.friendListContentPresenter.cleanChangedRights();
    this.updateRightsBySelectedNode();
    if (updateSharedRightsForDefaultImportGroup) {
      const defaultImportGroup: ProjectGroupVO = this.model.rootProjectGroup.groups.find((g: ProjectGroupVO) =>
        ProjectGroupUtils.isDefaultImportProjectGroupType(g.groupType)
      );
      if (defaultImportGroup) {
        this.friendListContentPresenter.removeSharedRightsByNode(defaultImportGroup);
        this.sharedObjectInfoDataService
          .getSharedProjectGroupInfoByGroupId(defaultImportGroup.id)
          .toPromise()
          .then((sharedInfo) => this.sharedInfoLoaded(sharedInfo))
          .catch((e) => {});
      }
    }
    this.toastrService.success(
      this.localeResourceManager.getValueByKey('toast.message.saved'),
      this.localeResourceManager.getValueByKey('toast.message.title.permissions')
    );
  }

  private saveSharedObjectInfo_failure(message: string): void {
    this.alertHelper.showError('global.title.error', 'shareProjectPopup.alert.message.save_shared_rights_failure', {translationParams: [message]});
  }

  public updateRightsBySelectedNode(): Subject<any> {
    return this.updateRightsByNode(this.model.currentSelectedItem);
  }

  private updateRightsByNode(item: ProjectGroupVO | ProjectHeadVO): Subject<any> {
    if (this.friendListContentPresenter.isCurrentUserRightsChanged()) {
      this.friendListContentPresenter.applyChangedSharedUserRightsToUploadedNodes(item);
    }
    const subject: ReplaySubject<any> = new ReplaySubject(1);
    this.friendListContentPresenter.updateSharedRightsByGroupNode(item).subscribe(() => {
      this.updateAllRightCheckBoxStates();
      subject.next(undefined);
      subject.complete();
    });
    return subject;
  }

  private findGroupById(id: number, groups: ProjectGroupVO[]): ProjectGroupVO {
    for (const group of groups) {
      if (group.id === id) {
        return group;
      }
      if (ArrayUtils.isNotEmpty(group.groups)) {
        const projectGroup: ProjectGroupVO = this.findGroupById(id, group.groups);
        if (projectGroup) {
          return projectGroup;
        }
      }
    }
    return null;
  }

  private findProjectById(id: number, groups: ProjectGroupVO[]): ProjectHeadVO {
    for (const group of groups) {
      if (ArrayUtils.isNotEmpty(group.projects)) {
        for (const projectHead of group.projects) {
          if (projectHead.id === id) {
            return projectHead;
          }
        }
      }
      if (ArrayUtils.isNotEmpty(group.groups)) {
        const project: ProjectHeadVO = this.findProjectById(id, group.groups);
        if (project) {
          return project;
        }
      }
    }
    return null;
  }
}
